// PATH: src/pages/admin/tabs/FixedEventsTab.jsx
// VERSION: 2.2 — Refactored for Dark Mode Support

import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Heading,
  Spinner,
  Textarea,
  Select,
  Switch,
  FormControl,
  FormLabel,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiRefreshCw, FiEdit2, FiTrash2, FiSave } from 'react-icons/fi';
import axiosInstance from '../../../api/axiosConfig';
import CloudinaryImageField from '../../../admin/components/CloudinaryImageField';

const WEEKDAY_LABELS = [
  'Domingo',
  'Lunes',
  'Martes',
  'Miércoles',
  'Jueves',
  'Viernes',
  'Sábado',
];

// Opciones de color para Chakra (colorScheme)
const COLOR_OPTIONS = [
  { value: 'accent', label: 'Accent (tema)' },
  { value: 'brand', label: 'Brand (principal)' },
  { value: 'purple', label: 'Morado' },
  { value: 'blue', label: 'Azul' },
  { value: 'teal', label: 'Turquesa' },
  { value: 'green', label: 'Verde' },
  { value: 'yellow', label: 'Amarillo' },
  { value: 'orange', label: 'Naranja' },
  { value: 'red', label: 'Rojo' },
  { value: 'pink', label: 'Rosado' },
  { value: 'gray', label: 'Gris' },
];

export default function FixedEventsTab() {
  const toast = useToast();

  // --- Theme Variables ---
  const headingColor = useColorModeValue('brand.800', 'text.default');
  const subTextColor = useColorModeValue('brand.500', 'text.muted');
  const sectionHeadingColor = useColorModeValue('brand.700', 'text.default');
  const theadBg = useColorModeValue('brand.100', 'whiteAlpha.200');
  const rowHoverBg = useColorModeValue('brand.50', 'whiteAlpha.100');
  const borderColor = useColorModeValue('brand.200', 'whiteAlpha.300');
  const inputBg = useColorModeValue('white', 'gray.700');
  const inputBorder = useColorModeValue('brand.300', 'whiteAlpha.300');
  const tableBorderColor = useColorModeValue('brand.200', 'whiteAlpha.300');
  const labelColor = useColorModeValue('brand.700', 'text.default');

  // --- BW Style Object (Dynamic) ---
  const bw = {
    box: {
      border: '1px solid',
      borderColor: borderColor,
      bg: useColorModeValue('white', 'bg.card'),
      color: useColorModeValue('gray.800', 'text.default'),
      borderRadius: 'xl',
      boxShadow: 'sm',
    },
    input: {
      border: '1px solid',
      borderColor: inputBorder,
      bg: inputBg,
      color: useColorModeValue('gray.800', 'text.default'),
      borderRadius: 'md',
      _placeholder: { color: useColorModeValue('brand.400', 'text.muted'), fontWeight: 'semibold' },
      _hover: { borderColor: useColorModeValue('brand.400', 'whiteAlpha.400') },
      _focus: {
        borderColor: 'accent.500',
        boxShadow: '0 0 0 1px var(--chakra-colors-accent-500)',
      },
    },
    btn: {
      borderRadius: 'md',
      bg: 'accent.500',
      border: '1px solid',
      borderColor: 'accent.500',
      color: 'white',
      fontWeight: 'semibold',
      _hover: {
        bg: 'accent.600',
        borderColor: 'accent.600',
        transform: 'translateY(-1px)',
        boxShadow: 'md',
      },
      _active: { transform: 'translateY(0)', boxShadow: 'sm' },
    },
    btnGhost: {
      borderRadius: 'md',
      bg: 'transparent',
      border: '1px solid',
      borderColor: useColorModeValue('brand.300', 'whiteAlpha.300'),
      color: useColorModeValue('brand.700', 'text.default'),
      fontWeight: 'semibold',
      _hover: { bg: useColorModeValue('brand.50', 'whiteAlpha.100'), borderColor: useColorModeValue('brand.400', 'whiteAlpha.400') },
    },
  };

  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({
    id: null,
    title: '',
    weekday: '0',
    time: '',
    place: '',
    description: '',
    image: '',
    color: 'accent',
    isActive: true,
    firstSundayOnly: false,
  });

  async function load() {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/fixed-events', {
        params: { includeInactive: 1 },
      });
      const items = Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
          ? data
          : [];
      setEvents(items);
    } catch (e) {
      console.error(e);
      toast({ status: 'error', title: 'Error cargando horarios' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function editRow(r) {
    setForm({
      id: r.id,
      title: r.title || '',
      weekday: String(r.weekday ?? '0'),
      time: r.time || '',
      place: r.place || '',
      description: r.description || '',
      image: r.image || '',
      color: r.color || 'accent',
      isActive: r.isActive !== false,
      firstSundayOnly: r.firstSundayOnly === true,
    });
  }

  function resetForm() {
    setForm({
      id: null,
      title: '',
      weekday: '0',
      time: '',
      place: '',
      description: '',
      image: '',
      color: 'accent',
      isActive: true,
      firstSundayOnly: false,
    });
  }

  async function save() {
    const payload = {
      title: form.title.trim(),
      weekday: Number(form.weekday),
      time: form.time || null,
      place: form.place || null,
      description: form.description || null,
      image: form.image || null,
      color: form.color || null,
      isActive: form.isActive,
      firstSundayOnly: form.firstSundayOnly,
    };

    if (!payload.title) {
      toast({ status: 'warning', title: 'Título requerido' });
      return;
    }
    if (
      Number.isNaN(payload.weekday) ||
      payload.weekday < 0 ||
      payload.weekday > 6
    ) {
      toast({ status: 'warning', title: 'Selecciona un día válido' });
      return;
    }

    try {
      if (form.id) {
        await axiosInstance.patch(`/fixed-events/${form.id}`, payload);
        toast({ status: 'success', title: 'Horario actualizado' });
      } else {
        await axiosInstance.post('/fixed-events', payload);
        toast({ status: 'success', title: 'Horario creado' });
      }
      resetForm();
      load();
    } catch (e) {
      console.error(e);
      toast({ status: 'error', title: 'No se pudo guardar' });
    }
  }

  async function remove(id) {
    if (!window.confirm('¿Eliminar horario fijo?')) return;
    try {
      await axiosInstance.delete(`/fixed-events/${id}`);
      toast({ status: 'success', title: 'Eliminado' });
      load();
    } catch (e) {
      console.error(e);
      toast({ status: 'error', title: 'No se pudo eliminar' });
    }
  }

  const weekdayLabelLower =
    WEEKDAY_LABELS[Number(form.weekday ?? 0)]?.toLowerCase() ?? 'día';

  return (
    <Box {...bw.box} p={4}>
      <Heading fontSize="lg" mb={1} color={headingColor}>
        Horarios (eventos fijos/recurrentes)
      </Heading>
      <Box fontSize="sm" mb={4} color={subTextColor}>
        Define los horarios fijos de la iglesia (por ejemplo, culto dominical, estudios entre
        semana). Se mostrarán en la página “Nuestro Horario”, en Calendario y en Próximos eventos.
      </Box>

      {/* Formulario responsivo */}
      <Flex gap={3} mb={3} wrap="wrap" align="flex-start">
        <Input
          {...bw.input}
          placeholder="Título"
          value={form.title}
          onChange={(e) =>
            setForm((f) => ({ ...f, title: e.target.value }))
          }
          maxW={{ base: '100%', md: '260px' }}
        />
        <Select
          {...bw.input}
          value={form.weekday}
          onChange={(e) =>
            setForm((f) => ({ ...f, weekday: e.target.value }))
          }
          maxW={{ base: '100%', md: '180px' }}
        >
          {WEEKDAY_LABELS.map((label, idx) => (
            <option key={idx} value={idx}>
              {label}
            </option>
          ))}
        </Select>
        <Input
          {...bw.input}
          placeholder="Hora (ej. 10:00 AM)"
          value={form.time}
          onChange={(e) =>
            setForm((f) => ({ ...f, time: e.target.value }))
          }
          maxW={{ base: '100%', md: '180px' }}
        />
        <Input
          {...bw.input}
          placeholder="Lugar (opcional)"
          value={form.place}
          onChange={(e) =>
            setForm((f) => ({ ...f, place: e.target.value }))
          }
          maxW={{ base: '100%', md: '260px' }}
        />
        <Select
          {...bw.input}
          value={form.color}
          onChange={(e) =>
            setForm((f) => ({ ...f, color: e.target.value }))
          }
          maxW={{ base: '100%', md: '200px' }}
        >
          {COLOR_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
        <FormControl
          display="flex"
          alignItems="center"
          maxW={{ base: '100%', md: '160px' }}
          mt={{ base: 2, md: 0 }}
        >
          <FormLabel
            htmlFor="isActiveFixed"
            mb="0"
            fontSize="sm"
            color={labelColor}
          >
            Activo
          </FormLabel>
          <Switch
            id="isActiveFixed"
            isChecked={form.isActive}
            onChange={(e) =>
              setForm((f) => ({ ...f, isActive: e.target.checked }))
            }
            colorScheme="green"
          />
        </FormControl>
        <FormControl
          display="flex"
          alignItems="center"
          maxW={{ base: '100%', md: '260px' }}
          mt={{ base: 2, md: 0 }}
        >
          <FormLabel
            htmlFor="firstSundayOnly"
            mb="0"
            fontSize="xs"
            color={labelColor}
          >
            Solo primer {weekdayLabelLower} del mes
          </FormLabel>
          <Switch
            id="firstSundayOnly"
            isChecked={form.firstSundayOnly}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                firstSundayOnly: e.target.checked,
              }))
            }
            colorScheme="purple"
          />
        </FormControl>
      </Flex>

      <Textarea
        {...bw.input}
        rows={3}
        placeholder="Descripción (opcional)"
        value={form.description}
        onChange={(e) =>
          setForm((f) => ({ ...f, description: e.target.value }))
        }
        mb={3}
      />

      <Box mb={3}>
        <CloudinaryImageField
          label="Imagen (opcional, Cloudinary)"
          value={form.image}
          onChange={(v) =>
            setForm((f) => ({ ...f, image: v }))
          }
          folder="fixed-events"
          previewW={420}
          previewH={240}
        />
      </Box>

      <Flex gap={2} mb={4} wrap="wrap">
        <Button {...bw.btn} leftIcon={<FiSave />} onClick={save}>
          {form.id ? 'Actualizar' : 'Crear'}
        </Button>
        {form.id && (
          <Button {...bw.btnGhost} onClick={resetForm}>
            Cancelar
          </Button>
        )}
        <Button
          {...bw.btnGhost}
          leftIcon={<FiRefreshCw />}
          onClick={load}
        >
          Refrescar
        </Button>
      </Flex>

      {loading ? (
        <Flex justify="center" align="center" py={8}>
          <Spinner thickness="3px" speed="0.65s" />
        </Flex>
      ) : (
        <Box overflowX="auto">
          <Table
            size="sm"
            variant="simple"
            sx={{
              'th, td, tr, thead, tbody': {
                borderColor: tableBorderColor,
              },
            }}
            border="1px solid"
            borderColor={tableBorderColor}
          >
            <Thead bg={theadBg}>
              <Tr>
                <Th borderColor={tableBorderColor}>ID</Th>
                <Th borderColor={tableBorderColor}>Título</Th>
                <Th borderColor={tableBorderColor}>Día</Th>
                <Th borderColor={tableBorderColor}>Hora</Th>
                <Th borderColor={tableBorderColor}>Color</Th>
                <Th borderColor={tableBorderColor}>Activo</Th>
                <Th borderColor={tableBorderColor}>1er día del mes</Th>
                <Th borderColor={tableBorderColor}>Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              {events.map((e) => (
                <Tr key={e.id} _hover={{ bg: rowHoverBg }}>
                  <Td borderColor={tableBorderColor}>{e.id}</Td>
                  <Td borderColor={tableBorderColor}>{e.title}</Td>
                  <Td borderColor={tableBorderColor}>
                    {WEEKDAY_LABELS[Number(e.weekday ?? 0)]}
                  </Td>
                  <Td borderColor={tableBorderColor}>{e.time || ''}</Td>
                  <Td borderColor={tableBorderColor}>
                    {e.color || 'accent'}
                  </Td>
                  <Td borderColor={tableBorderColor}>
                    {e.isActive !== false ? 'Sí' : 'No'}
                  </Td>
                  <Td borderColor={tableBorderColor}>
                    {e.firstSundayOnly ? 'Sí' : 'No'}
                  </Td>
                  <Td
                    borderColor={tableBorderColor}
                    whiteSpace="nowrap"
                  >
                    <Button
                      {...bw.btnGhost}
                      size="xs"
                      mr={2}
                      leftIcon={<FiEdit2 />}
                      onClick={() => editRow(e)}
                    >
                      Editar
                    </Button>
                    <Button
                      {...bw.btnGhost}
                      size="xs"
                      leftIcon={<FiTrash2 />}
                      onClick={() => remove(e.id)}
                    >
                      Eliminar
                    </Button>
                  </Td>
                </Tr>
              ))}
              {events.length === 0 && (
                <Tr>
                  <Td
                    colSpan={8}
                    textAlign="center"
                    py={4}
                    borderColor={tableBorderColor}
                  >
                    No hay horarios registrados.
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}
